import { lazy, Suspense } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import Application from '@app/Application';
import { AppGate, OnboardingGate, RequireStage } from '@entities/User';
import AdminPage from '@pages/Admin';
import AuthPage from '@pages/Auth';
import TopicCardsPage from '@pages/TopicCards';
import CommunityPage from '@pages/Community';
import ConfirmEmailPage from '@pages/ConfirmEmail';
import CramPage from '@pages/Cram';
import DashboardPage from '@pages/Dashboard';
import ForgotPasswordPage from '@pages/ForgotPassword';
import InstallPage from '@pages/Install';
import ModuleDetailPage from '@pages/ModuleDetail';
import ModulePreviewPage from '@pages/ModulePreview';
import NotificationsPage from '@pages/Notifications';
import OAuthErrorPage from '@pages/OAuthError';
import OAuthSuccessPage from '@pages/OAuthSuccess';
import OnboardingPage, { OnboardingResultPage, OnboardingTrainingPage } from '@pages/Onboarding';
import PaymentSuccessPage from '@pages/PaymentSuccess';
import PlansPage from '@pages/Plans';
import ProfilePage from '@pages/Profile';
import PublicModuleDetailPage from '@pages/PublicModuleDetail';
import RepeatPage from '@pages/Repeat';
import ResetPasswordPage from '@pages/ResetPassword';
import SessionPage from '@pages/Session';
import TrainingPage from '@pages/Training';
import UnsubscribePage from '@pages/Unsubscribe';
import WordsPage from '@pages/Words';
import { buildComposerState, paths } from '@shared/config';

// «Полезное» — отдельным чанком: в нём таблица глаголов с описаниями (~150 КБ данных).
const UsefulPage = lazy(() => import('@pages/Useful'));
// Игра «Выживи в Лондоне» — отдельным чанком: пиксельный движок и экран игры нужны только в ней.
const StoryPlayPage = lazy(() => import('@pages/StoryPlay'));
const StoryMissionPage = lazy(() => import('@pages/StoryMission'));
const StoryBossPage = lazy(() => import('@pages/StoryBoss'));
const LondonPage = lazy(() => import('@pages/London'));
const StoriesPage = lazy(() => import('@pages/Stories'));
// Демо без регистрации — с 2026-11-03 короткая сцена новой игры (Олли); старое демо — `@pages/StoryDemo` до выката.
const StoryDemoPage = lazy(() => import('@pages/LondonDemo'));
const DemoWordsPage = lazy(() => import('@pages/DemoWords'));
const DailyTaskPage = lazy(() => import('@pages/DailyTask'));
// «Грамматика» — тропинка, урок и упражнение своими чанками.
const GrammarPage = lazy(() => import('@pages/Grammar'));
const GrammarLessonPage = lazy(() => import('@pages/GrammarLesson'));
const GrammarExercisePage = lazy(() => import('@pages/GrammarExercise'));
const GrammarReviewPage = lazy(() => import('@pages/GrammarReview'));
const StoryMistakesPage = lazy(() => import('@pages/StoryMistakes'));

/** Страницы «Из словаря» больше нет (решение владельца 2026-10-21): старые адреса — закладки, письма,
 *  история — ведут на «Слова и модули» со строкой создания в фокусе и модулем из адреса. */
function ComposerRedirect() {
  const { id } = useParams();
  return <Navigate to={paths.words} replace state={buildComposerState(id)} />;
}

/** «Модули» стали вкладкой «Слов и модулей» (2026-10-23): старый адрес из закладок и писем ведёт туда,
 *  «Удалённые» — тоже. */
function UnitsRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={params.get('filter') === 'deleted' ? paths.wordsDeleted : paths.words} replace />;
}

const routesConfig: RouteObject[] = [
  {
    path: paths.install,
    element: <InstallPage />,
  },
  {
    // Ссылка «Не присылать напоминания» из письма: без входа, отписка POST-запросом после загрузки.
    path: paths.unsubscribe,
    element: <UnsubscribePage />,
  },
  {
    path: paths.auth,
    element: (
      <RequireStage stage="auth">
        <AuthPage mode="signin" />
      </RequireStage>
    ),
  },
  {
    path: paths.signUp,
    element: (
      <RequireStage stage="auth">
        <AuthPage mode="signup" />
      </RequireStage>
    ),
  },
  {
    path: paths.forgotPassword,
    element: (
      <RequireStage stage="auth">
        <ForgotPasswordPage />
      </RequireStage>
    ),
  },
  {
    path: paths.resetPassword,
    element: <ResetPasswordPage />,
  },
  {
    path: paths.confirmEmail,
    element: <ConfirmEmailPage />,
  },
  {
    path: paths.oauthSuccess,
    element: <OAuthSuccessPage />,
  },
  {
    path: paths.oauthError,
    element: <OAuthErrorPage />,
  },
  {
    path: paths.onboarding,
    element: (
      <OnboardingGate>
        <OnboardingPage />
      </OnboardingGate>
    ),
  },
  {
    // Первая тренировка знакомства идёт до finish-onboarding: /session под AppGate вернул бы
    // ученика в /onboarding, поэтому свой адрес под OnboardingGate с той же сценой тренировки.
    path: paths.onboardingTraining,
    element: (
      <OnboardingGate>
        <OnboardingTrainingPage />
      </OnboardingGate>
    ),
  },
  {
    path: paths.onboardingResult,
    element: (
      <OnboardingGate>
        <OnboardingResultPage />
      </OnboardingGate>
    ),
  },
  {
    path: paths.session,
    element: (
      <AppGate>
        <SessionPage />
      </AppGate>
    ),
  },
  {
    // Новая игра «Выживи в Лондоне» (этап 1, docs/product/game-stage1.md) — на весь экран, своим чанком.
    path: paths.london,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <LondonPage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Миссия дня v2 — разговор с жителем на весь экран, как в новой игре.
    path: paths.storyMission,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <StoryMissionPage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Бой с Граммозавром v2 — арена на весь экран.
    path: paths.storyBoss,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <StoryBossPage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Игра «Выживи в Лондоне» — на весь экран, без боковой панели, как тренировка.
    path: paths.storyPlay,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <StoryPlayPage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Подборка повторения грамматики — на весь экран, как упражнение. Статичный адрес важнее /grammar/:slug.
    path: paths.grammarReview,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <GrammarReviewPage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Демо миссии без регистрации (2026-10-25): ссылка «Попробовать» с лендинга. Вне AppGate — гостя не уводит на вход.
    path: paths.storyDemo,
    element: (
      <Suspense fallback={null}>
        <StoryDemoPage />
      </Suspense>
    ),
  },
  {
    // Слова из итогов демо после регистрации: под AppGate — гостя уведёт на вход и вернёт сюда.
    path: paths.demoWords,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <DemoWordsPage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Упражнение грамматики — на весь экран, без меню и панели вкладок, как тренировка.
    path: paths.grammarExercise,
    element: (
      <AppGate>
        <Suspense fallback={null}>
          <GrammarExercisePage />
        </Suspense>
      </AppGate>
    ),
  },
  {
    // Режим ознакомления — полноэкранный иммерсивный поток (без сайдбара), как сессия.
    path: paths.modulePreview,
    element: (
      <AppGate>
        <ModulePreviewPage />
      </AppGate>
    ),
  },
  {
    // Возврат из YooKassa. НЕ оборачиваем в AppGate (как и OAuthSuccess): после
    // full-page-редиректа сессию восстанавливает axios-интерцептор по refresh-cookie
    // при первом же /payments/{id}/sync. Если восстановить не удалось — страница
    // сама покажет «оплата принята, тариф активируется» вместо редиректа в /auth
    // (тариф всё равно выдаёт вебхук на стороне сервера).
    path: paths.paymentSuccess,
    element: <PaymentSuccessPage />,
  },
  {
    path: '/',
    element: (
      <AppGate>
        <Application />
      </AppGate>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'training', element: <TrainingPage /> },
      { path: 'words', element: <WordsPage /> },
      { path: 'units', element: <UnitsRedirect /> },
      { path: 'units/:id', element: <ModuleDetailPage /> },
      { path: 'units/:id/cram', element: <CramPage /> },
      // Ссылка из письма-напоминания → тренировка модуля (гость вернётся сюда после входа).
      { path: 'repeat', element: <RepeatPage /> },
      { path: 'units/:id/cards/new', element: <ComposerRedirect /> },
      { path: 'cards/new', element: <ComposerRedirect /> },
      { path: 'cards/topics', element: <TopicCardsPage /> },
      // «Публичные модули» — теперь вкладка «Готовые» на «Словах и модулях» (2026-10-23).
      { path: 'library', element: <Navigate to={paths.wordsReady} replace /> },
      { path: 'library/:id', element: <PublicModuleDetailPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'plans', element: <PlansPage /> },
      { path: 'community', element: <CommunityPage /> },
      {
        path: 'daily',
        element: (
          <Suspense fallback={null}>
            <DailyTaskPage />
          </Suspense>
        ),
      },
      {
        path: 'stories',
        element: (
          <Suspense fallback={null}>
            <StoriesPage />
          </Suspense>
        ),
      },
      {
        path: 'stories/mistakes',
        element: (
          <Suspense fallback={null}>
            <StoryMistakesPage />
          </Suspense>
        ),
      },
      { path: 'useful', element: <Navigate to={paths.usefulVerbs} replace /> },
      // «Грамматика» была вкладкой «Полезного» — старый адрес ведёт в раздел.
      { path: 'useful/grammar', element: <Navigate to={paths.grammar} replace /> },
      {
        path: 'grammar',
        element: (
          <Suspense fallback={null}>
            <GrammarPage />
          </Suspense>
        ),
      },
      {
        path: 'grammar/:slug',
        element: (
          <Suspense fallback={null}>
            <GrammarLessonPage />
          </Suspense>
        ),
      },
      {
        path: 'useful/:section',
        element: (
          <Suspense fallback={null}>
            <UsefulPage />
          </Suspense>
        ),
      },
      // Гость, открывший /admin вручную, получит пустой дашборд: бэк ответит 403
      // NOT_ADMIN, страница покажет состояние «нет доступа». Вкладку в сайдбаре
      // видят только ADMIN.
      { path: 'admin', element: <AdminPage /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to={paths.dashboard} replace />,
  },
];

export default routesConfig;
